import { type FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button/Button';
import { ModalContainer, ModalHeader, ModalActions } from '@/utils/modal';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import * as Auth from '@/support/auth';
import {
  MAX_PUBLISHED_VIEWS_PER_TABLE,
  partitionViews,
  validateViewName,
  VIEW_NAME_MAX_LENGTH,
} from '@/utils/userTableSettings/UserTableSettings.utils';
import ViewVisibilityField from '@/components/UI/Table/Reusable/APITable/ViewSelector/ViewVisibilityField/ViewVisibilityField';
import styles from './EditViewModal.module.scss';
import type { UserTableView, UserTableViewApiId, UserTableViewTableName } from '@/model/UserTableView.model.ts';

export interface EditViewModalProps {
  tableName: UserTableViewTableName;
  view: UserTableView;
  onViewDeleted: (viewApiId: UserTableViewApiId) => void;
  closeModal: () => void;
}

interface EditViewFormData {
  viewName: string;
}

const EditViewModal: FC<EditViewModalProps> = ({ tableName, view, onViewDeleted, closeModal }) => {
  const { updateView, deleteView, getViews } = useUserTableViews();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [published, setPublished] = useState(view.published);

  const views = getViews(tableName);
  const { published: publishedViews } = partitionViews(views);
  const isTenantAdmin = Auth.isCurrentTeamOwner() === true;
  const isBusy = isSaving || isDeleting;
  const visibilityChanged = published !== view.published;
  const atPublishedLimit =
    publishedViews.filter((other) => other.api_id !== view.api_id).length >= MAX_PUBLISHED_VIEWS_PER_TABLE;

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<EditViewFormData>({
    mode: 'onChange',
    defaultValues: { viewName: view.name },
  });

  /**
   * A name free among personal views may be taken among published ones, and vice versa. This runs
   * from an effect rather than the change handler because the validator registered with the form
   * still closes over the previous group until the re-render lands.
   */
  const revalidatedFor = useRef(published);
  useEffect(() => {
    if (revalidatedFor.current !== published) {
      revalidatedFor.current = published;
      void trigger('viewName');
    }
  }, [published, trigger]);

  const onSubmit = async (data: EditViewFormData) => {
    const name = data.viewName.trim();

    setIsSaving(true);
    const success = await updateView(tableName, view.api_id, { name, published });

    if (success) {
      closeModal();
      successToast(`Table view '${name}' updated`);
    } else {
      errorToast('Failed to update view. Please try again.');
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isBusy) {
      return;
    }
    setIsDeleting(true);
    const success = await deleteView(view.api_id);

    if (success) {
      onViewDeleted(view.api_id);
      closeModal();
      successToast(`Table view '${view.name}' deleted`);
    } else {
      errorToast('Failed to delete view. Please try again.');
      setIsDeleting(false);
    }
  };

  const fieldError = errors.viewName?.message;

  return (
    <ModalContainer size="narrow" data-test-component="EditViewModal">
      <ModalHeader title="Edit view" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3">
          <Banner info dismiss={false} className="mb3 f6" testId="edit-view-modal__hint">
            This will not change the columns, sort, page size, and filters saved to this view.
          </Banner>

          <div className={styles.inputGroup}>
            <label htmlFor="edit-view-name" className={styles.label}>
              View name
            </label>
            <input
              id="edit-view-name"
              type="text"
              className={_isNotBlank(fieldError) ? 'input__error' : ''}
              placeholder="Enter a name for this view..."
              maxLength={VIEW_NAME_MAX_LENGTH}
              autoFocus
              data-test-component="EditViewModal"
              data-test-element="name-input"
              {...register('viewName', {
                validate: (value) => validateViewName(value, views, published, view.api_id),
              })}
            />
            {_isNotBlank(fieldError) && (
              <small className="red db" data-test-component="EditViewModal" data-test-element="error">
                {fieldError}
              </small>
            )}
          </div>

          {isTenantAdmin && (
            <ViewVisibilityField
              published={published}
              onChange={setPublished}
              disabled={isBusy}
              publishedLimitReached={atPublishedLimit}
              testId="edit-view-modal__visibility"
            />
          )}
        </div>

        <ModalActions
          submitBtnText="Save changes"
          submitButtonProps={{
            disabled: isBusy || !isValid || (!isDirty && !visibilityChanged),
            loading: isSaving,
          }}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          cancelButtonProps={{ disabled: isBusy }}
          asideComponent={
            <span className="flex items-center">
              <Button
                stateless
                onClick={handleDelete}
                disabled={isBusy}
                loading={isDeleting}
                testId="edit-view-modal__delete-button"
              >
                <span className="red">Delete view</span>
              </Button>
            </span>
          }
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default EditViewModal;
